import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { FlightSelectors } from "@checkout/index";
import { trackEvent } from "@hopper-b2b/api";
// eslint-disable-next-line @nx/enforce-module-boundaries
import { shopSelectors } from "@hopper-b2b/flights";
import { I18nMarkup, Trans, useI18nContext } from "@hopper-b2b/i18n";
import {
  PRICE_DROP_VIEWED,
  VIEWED_PRICE_DROP_DETAILS,
} from "@hopper-b2b/types";
import { ButtonWrap } from "@hopper-b2b/ui";
import { useEnablePriceDropProtection } from "@hopper-b2b/utilities";

import PriceDropProtectionIconDark from "../../../assets/client/darkMode/price-drop-protection-icon.svg";
import PriceDropProtectionIcon from "../../../assets/client/price-drop-protection-icon.svg";
import PriceDropProtectionIconHalfsheet from "../../../assets/client/uv/price-drop-protection-lavender-icon.svg";
import { getLocalStorageTrackingEvents } from "../../../hooks/useFlightLocalStorageTracking";
import {
  isUserTypeCookieUltravioleta,
  useDarkModePreferred,
} from "../../../utils/colors";
import { FintechBanner } from "../../FintechBanner";
import { FintechHalfSheet } from "../../FintechHalfSheet";
import { TermsAndConditionsContext } from "../../TermsAndConditionsWrapper/component";
import "./styles.scss";

interface FlightPriceDropProtectionBannerProps {
  isEligible: boolean;
  title?: string;
  subtitle?: string | JSX.Element;
  showMore?: boolean;
  hideBrand?: boolean;
  className?: string;
  children?: ReactNode;
}

const FlightPriceDropProtectionBanner = ({
  isEligible,
  title,
  subtitle,
  showMore = false,
  hideBrand = false,
  className,
  children,
}: FlightPriceDropProtectionBannerProps) => {
  const { t } = useI18nContext();
  const { setOpenModal } = useContext(TermsAndConditionsContext);
  const isDarkMode = useDarkModePreferred();

  const enabledPriceDropProtection = useEnablePriceDropProtection();
  const isUserUltravioletta = isUserTypeCookieUltravioleta();
  const [openHalfSheet, setOpenHalfSheet] = useState(false);
  const [openSeeRules, setOpenSeeRules] = useState(false);
  const priceDropViewedProperties = useSelector(
    shopSelectors.getPriceDropProperties
  );
  const localStorageTrackingEventsKey = useSelector(
    FlightSelectors.getLocalStorageKey
  );
  const handleOpenHalfSheet = useCallback(() => {
    setOpenHalfSheet(true);
    // Pass in properties if exist
    const properties = getLocalStorageTrackingEvents(
      localStorageTrackingEventsKey
    );
    trackEvent({
      eventName: VIEWED_PRICE_DROP_DETAILS,
      properties: {
        ...priceDropViewedProperties,
        ...properties?.properties,
        page: "flight_list",
      },
    });
  }, [priceDropViewedProperties, localStorageTrackingEventsKey]);

  const handleCloseHalfSheet = useCallback(() => {
    setOpenHalfSheet(false);
  }, []);

  const handleCloseSeeRules = useCallback(() => {
    setOpenSeeRules(false);
  }, []);

  const bannerCopy = useMemo(() => {
    // check for undefined to allow tenants to not have a title by sending null
    return {
      title:
        title !== undefined ? (
          title
        ) : (
          <I18nMarkup
            className="default-text"
            tKey={"priceDropProtectionIncludedBold"}
          />
        ),
      subtitle: subtitle || undefined,
    };
  }, [subtitle, title]);

  const isPDPEnabledAndEligible =
    isEligible && isUserUltravioletta && enabledPriceDropProtection;

  useEffect(() => {
    if (isPDPEnabledAndEligible) {
      // Pass in properties if exist
      const properties = getLocalStorageTrackingEvents(
        localStorageTrackingEventsKey
      );
      trackEvent({
        eventName: PRICE_DROP_VIEWED,
        properties: {
          ...priceDropViewedProperties,
          ...properties?.properties,
          page: "flight_list",
        },
      });
    }
  }, []);

  return isPDPEnabledAndEligible ? (
    <>
      <FintechBanner
        onClick={showMore ? handleOpenHalfSheet : null}
        fintechIcon={
          isDarkMode ? PriceDropProtectionIconDark : PriceDropProtectionIcon
        }
        hideBrand={hideBrand}
        title={bannerCopy.title}
        subtitle={bannerCopy.subtitle}
        className={className}
      />
      {showMore ? (
        <>
          <FintechHalfSheet
            isOpen={openHalfSheet}
            onClose={handleCloseHalfSheet}
            icon={PriceDropProtectionIconHalfsheet}
            title={t("priceDropHalfSheetTitle")}
            description={
              <div>
                <p>{t("priceDropHalfSheetIntro")}</p>
                <p>
                  {t("priceDropHalfSheetDescription")}{" "}
                  <ButtonWrap
                    className="nubank-terms-container"
                    onClick={() => setOpenSeeRules(true)}
                  >
                    <p>{t("seeRules")}</p>
                  </ButtonWrap>
                </p>
              </div>
            }
          />
          <FintechHalfSheet
            isOpen={openSeeRules}
            onClose={handleCloseSeeRules}
            title={t("priceDropProtectionFaq.title")}
            description={
              <div className="faq">
                <p>{t("priceDropProtectionFaq.introContent")}</p>
                <p>{t("priceDropProtectionFaq.details")}</p>
                <ul>
                  <li>{t("priceDropProtectionFaq.first")}</li>
                  <li>{t("priceDropProtectionFaq.second")}</li>
                  <li>{t("priceDropProtectionFaq.third")}</li>
                  <li>{t("priceDropProtectionFaq.fourth")}</li>
                  <li>{t("priceDropProtectionFaq.fifth")}</li>
                </ul>
                <div className="terms-and-conditions">
                  <Trans
                    i18nKey={"priceDropProtectionFaq.termsAndConditionsEntry"}
                    components={[
                      <ButtonWrap
                        className="terms-link"
                        onClick={() => setOpenModal(true)}
                      />,
                    ]}
                  />
                </div>
              </div>
            }
          />
        </>
      ) : null}
    </>
  ) : null;
};

export default FlightPriceDropProtectionBanner;
